import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useIntl } from '@grandvisionhq/state';
import { formatPrice } from '@grandvisionhq/utils';
import { PaymentForm } from '../../../component/PaymentForm/PaymentForm.js';
import { usePaymentsUIContext } from '../../../context/PaymentsUI/context.js';
import { useGetCartPaymentMethods } from '../../../hooks/use-get-cart-payment-methods.js';
import { usePaymentMethodLabelsMap } from '../../../hooks/use-payment-method-labels-map.js';
import { PaymentMethod } from '../../../models/cart-payment-methods/cart-payment-methods.js';
import { usePaymentMethodForm } from './use-payment-method-form.js';

var useGetPaymentMethodList = (cartId1, onPaymentCompleteHandler, selectedDonation, onDonationSelected, totalPrice, googlePayActions)=>{
    const { titleLabels  } = usePaymentMethodLabelsMap();
    const [getPaymentMethods, queryResult] = useGetCartPaymentMethods();
    const { isProcessingOrder  } = usePaymentsUIContext();
    const { locale , currency  } = useIntl();
    const loadPaymentMethods = React.useCallback((cartId)=>{
        getPaymentMethods(cartId);
    }, [
        getPaymentMethods
    ]);
    React.useEffect(()=>{
        if (cartId1) {
            loadPaymentMethods(cartId1);
        }
    }, [
        cartId1,
        getPaymentMethods,
        loadPaymentMethods
    ]);
    const [renderPaymentForm, selectedPaymentDetails, resetPaymentDetails] = usePaymentMethodForm(googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick, googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize);
    const paymentMethods = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.data;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.data
    ]);
    const error = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.error;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.error
    ]);
    const loading = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading
    ]);
    const called = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.called;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.called
    ]);
    const selectedPaymentData = React.useMemo(()=>selectedPaymentDetails
    , [
        selectedPaymentDetails
    ]);
    const onHandlePaymentAction = React.useCallback(()=>{
        onPaymentCompleteHandler(selectedPaymentDetails);
    }, [
        onPaymentCompleteHandler,
        selectedPaymentDetails
    ]);
    const paymentMethodItems = React.useMemo(()=>{
        if (!paymentMethods || error) {
            return [];
        }
        const items = paymentMethods.map((_param)=>{
            var { provider , name , icons , paymentMethodPrice  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
                "provider",
                "name",
                "icons",
                "paymentMethodPrice"
            ]);
            var ref, ref1;
            var ref2;
            const item = (ref2 = React.Children.toArray(icons)) !== null && ref2 !== void 0 ? ref2 : [];
            let formConfig;
            const paymentMethodList = (ref = rest) === null || ref === void 0 ? void 0 : ref.items;
            const cardBrands = (ref1 = rest) === null || ref1 === void 0 ? void 0 : ref1.brands;
            if (paymentMethodList) {
                formConfig = {
                    items: paymentMethodList
                };
            }
            if (cardBrands) {
                formConfig = {
                    brands: cardBrands
                };
            }
            if (name == PaymentMethod.GOOGLE_PAY && (googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick) && (googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize)) {
                formConfig = {
                    onClick: googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick,
                    onAuthorized: googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize
                };
            }
            const formComponent = renderPaymentForm({
                type: name,
                provider,
                config: formConfig
            });
            var ref3;
            return {
                value: name,
                label: titleLabels[name],
                subtext: (paymentMethodPrice === null || paymentMethodPrice === void 0 ? void 0 : paymentMethodPrice.additionalCosts) ? `+ ${formatPrice({
                    locale,
                    value: paymentMethodPrice.additionalCosts,
                    currency,
                    isCentAmount: true,
                    fixedDecimals: true
                })}` : undefined,
                id: `${provider}-${name}`,
                disabled: isProcessingOrder,
                aside: item,
                content: /*#__PURE__*/ React.createElement(PaymentForm, swcHelpers.extends({
                    paymentMethod: name,
                    paymentProvider: provider,
                    form: formComponent,
                    onHandlePaymentAction: onHandlePaymentAction,
                    isActionDisabled: false,
                    isActionLoading: false,
                    selectedDonation: selectedDonation,
                    onDonationSelected: onDonationSelected,
                    totalPrice: (ref3 = paymentMethodPrice === null || paymentMethodPrice === void 0 ? void 0 : paymentMethodPrice.emulatedTotalCartPrice) !== null && ref3 !== void 0 ? ref3 : totalPrice
                }, 'donations' in rest && {
                    donations: rest.donations
                }))
            };
        });
        return items;
    }, [
        currency,
        error,
        googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick,
        googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize,
        isProcessingOrder,
        locale,
        onDonationSelected,
        onHandlePaymentAction,
        paymentMethods,
        renderPaymentForm,
        selectedDonation,
        titleLabels,
        totalPrice, 
    ]);
    return [
        React.useMemo(()=>paymentMethodItems
        , [
            paymentMethodItems
        ]),
        selectedPaymentData,
        resetPaymentDetails,
        {
            error,
            called,
            loading,
            data: paymentMethods || []
        }, 
    ];
};

export { useGetPaymentMethodList };
//# sourceMappingURL=use-get-payment-method-list.js.map
